import React, { useEffect, useRef } from 'react';
import './AdmissionsOverview.css';
import usePageTitle from '../hooks/usePageTitle';
import { useLanguage } from '../hooks/useLanguage';
import textPin from '../assets/text_pin.png';
import educationPin from '../assets/education_pin.png';
import governmentPin from '../assets/government_pin.png';
import landscapeImage from '../assets/admissions_landscape.png';

const AdmissionsOverview: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('admissions.title'));
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Remove animation classes first to reset
    const allInitialElements = document.querySelectorAll('.page-title, .intro-section h2, .intro-section p');
    allInitialElements.forEach(element => {
      element.classList.remove('animate-in-initial');
    });
    
    // Force reflow to ensure the removal is processed
    void document.body.offsetHeight;
    
    // Animate initial content on page load
    // Title first
    const titleElement = document.querySelector('.page-title');
    if (titleElement) {
      setTimeout(() => {
        titleElement.classList.add('animate-in-initial');
      }, 200);
    }
    
    // Intro section elements together
    const introElements = document.querySelectorAll('.intro-section h2, .intro-section p');
    if (introElements.length > 0) {
      setTimeout(() => {
        introElements.forEach(element => {
          element.classList.add('animate-in-initial');
        });
      }, 410);
    }

    // Set up intersection observer for scroll animations
    const scrollElements = document.querySelectorAll('.pillar-card, .pillars-section h2, .landscape-content h2, .landscape-content p');
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !entry.target.classList.contains('animate-in-scroll')) {
            entry.target.classList.add('animate-in-scroll');
          }
        });
      },
      {
        threshold: [0, 0.1],
        rootMargin: '0px 0px -20% 0px'
      }
    );

    scrollElements.forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="admissions-overview">
      <div className="hero-section">
        <div className="hero-content">
          <h1 className="page-title">{t('admissions.title')}</h1>
          <div className="decorative-line"></div>
        </div>
      </div>

      <div className="vertical-line"></div>
      
      <div className="content-sections">
        <section className="intro-section">
          <div className="icon-circle">
            <img src={educationPin} alt="" className="sidebar-icon" />
          </div>
          <h2>{t('admissions.holisticTitle')}</h2>
          <p>{t('admissions.holisticText')}</p>
        </section>

        <section className="pillars-section">
          <div className="icon-circle">
            <img src={textPin} alt="" className="sidebar-icon" />
          </div>
          <h2>{t('admissions.pillarsTitle')}</h2>

          <div className="pillars-list">
            <div className="pillar-card" data-numeral="I">
              <h3>{t('admissions.pillar1Title')}</h3>
              <p>{t('admissions.pillar1Text')}</p>
            </div>

            <div className="pillar-card" data-numeral="II">
              <h3>{t('admissions.pillar2Title')}</h3>
              <p>{t('admissions.pillar2Text')}</p>
            </div>

            <div className="pillar-card" data-numeral="III">
              <h3>{t('admissions.pillar3Title')}</h3>
              <p>{t('admissions.pillar3Text')}</p>
            </div>

            <div className="pillar-card" data-numeral="IV">
              <h3>{t('admissions.pillar4Title')}</h3>
              <p>{t('admissions.pillar4Text')}</p>
            </div>

            <div className="pillar-card" data-numeral="V">
              <h3>{t('admissions.pillar5Title')}</h3>
              <p>{t('admissions.pillar5Text')}</p>
            </div>

            <div className="pillar-card" data-numeral="VI">
              <h3>{t('admissions.pillar6Title')}</h3>
              <p>{t('admissions.pillar6Text')}</p>
            </div>
          </div>
        </section>

        <section className="landscape-section">
          <div className="landscape-background">
            <img src={landscapeImage} alt="" className="landscape-bg-image" />
          </div>
          <div className="landscape-content">
            <div className="icon-circle">
              <img src={governmentPin} alt="" className="sidebar-icon" />
            </div>
            <h2>{t('admissions.landscapeTitle')}</h2>
            <p>{t('admissions.landscapeText1')}</p>
            <p>{t('admissions.landscapeText2')}</p>
            <p>{t('admissions.landscapeText3')}</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdmissionsOverview;